<template>
    <v-container class="fill-height pa-0">
        <v-row justify="center" class="fill-height">
            <!-- CATEGORY FILTERS -->
            <v-col v-if="categoryHasFilterOptions" cols="12" md="3" class="sticky-left" style="align-self: flex-start">
                <v-toolbar flat dense>
                    <v-toolbar-title>{{ $t('Filter') }}</v-toolbar-title>
                    <v-spacer />
                    <v-btn v-if="isFilterApplied" text icon @click="clearFilter">
                        <v-icon>mdi-filter-variant-remove</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-divider />
                <v-list dense>
                    <!-- BRANDS -->
                    <v-list-group v-if="currentCategory?.filterOptions?.brands?.length > 1" :value="true">
                        <template v-slot:activator>
                            <v-list-item-title>{{ $t('Brand') }}</v-list-item-title>
                        </template>
                        <v-list-item-group v-model="selectedBrands" multiple @change="applyBrandFilter">
                            <v-list-item v-for="brand in currentCategory.filterOptions.brands" :key="brand.brandId"
                                :value="brand.brandId">
                                <template #default="{ active }">
                                    <v-list-item-action class="mr-2">
                                        <v-checkbox :input-value="active"></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ brand.brandName }}</v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list-group>

                    <!-- ATTRIBUTES -->
                    <v-list-group v-for="attribute in currentCategory?.filterOptions?.attributes"
                        :key="attribute.attributeId">
                        <template v-slot:activator>
                            <v-list-item-title>{{ attribute.attributeName }}</v-list-item-title>
                        </template>
                        <v-list-item-group v-model="attribute.selectedValues" multiple @change="setAttributeFilter">
                            <v-list-item v-for="value in attribute.attributeValues" :key="value.attributeValueId"
                                :value="value.attributeValueId">
                                <template #default="{ active }">
                                    <v-list-item-action class="mr-2">
                                        <v-checkbox :input-value="active"></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ value.attributeValue }}</v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list-group>
                </v-list>
                <v-divider />
            </v-col>

            <!-- CATALOG ITEMS-->
            <v-col :cols="categoryHasFilterOptions ? 9 : 12" class="fill-height">

                <!-- CATEGORY HEADER -->
                <v-row v-if="currentCategoryName">
                    <v-col cols="12">
                        <v-toolbar flat dense>
                            <v-toolbar-title>{{ currentCategoryName }}</v-toolbar-title>
                        </v-toolbar>
                    </v-col>
                </v-row>
                <v-row v-if="loading" justify="center" class="my-16">
                    <v-progress-circular indeterminate size="64" color="primary" />
                </v-row>
                <template v-else>
                    <!-- SUBCATEGORIES -->
                    <v-row v-if="currentCategory?.subCategories?.length">
                        <v-col cols="12" md="4" lg="3" v-for="subCategory in currentCategory.subCategories"
                            :key="subCategory.id" align="center">
                            <SubCategoryCard :subCategory="subCategory" @select="selectSubCategory" />
                        </v-col>
                    </v-row>

                    <!-- CATALOG ITEMS -->
                    <v-row v-if="itemList?.length">

                        <v-col cols="12" md="4" lg="3" v-for="(catalogItem, index) of itemList" :key="index"
                            align="center">
                            <CatalogItemCard :catalogItem="catalogItem" />
                        </v-col>
                    </v-row>
                </template>
                <!-- LOAD MORE -->
                <v-row justify="center">
                    <v-btn v-if="loadMore" class="my-3" :loading="loading" color="primary" @click="loadMoreItems">
                        {{ $t('Load_more') }}
                    </v-btn>
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from "@/store";
import { mapGetters } from "vuex";
import CatalogItemCard from "../components/CatalogItemCard.vue";
import SubCategoryCard from "../components/SubCategoryCard.vue";

export default {
    name: "ProductCategory",

    components: {
        CatalogItemCard,
        SubCategoryCard,
    },

    props: {
        id: {
            type: [String, Number],
            default: null
        }
    },

    data() {
        return {
            selectedBrands: []
        };
    },

    watch: {
        id: {
            immediate: true,
            async handler(id) {
                if (id) {
                    await store.dispatch("catalog/fetchCategory", Number(id));
                } else {
                    store.commit("catalog/setCurrentCategory", null);
                }
                this.scrollToTop();
            }
        }
    },

    computed: {
        ...mapGetters({
            mainCategories: "catalog/mainCategories",
            currentCategory: "catalog/currentCategory",
            currentCategoryName: "catalog/currentCategoryName",
            itemList: "catalog/itemList",
            loading: "catalog/loading",
            loadMore: "catalog/loadMore",
        }),
        categoryHasFilterOptions() {
            return this.currentCategory?.filterOptions?.brands?.length > 1 || this.currentCategory?.filterOptions?.attributes?.length;
        },
        isFilterApplied() {
            if (this.selectedBrands?.length) {
                return true;
            }
            if (this.currentCategory?.filterOptions?.attributes?.some(a => a.selectedValues?.length)) {
                return true;
            }
            return false;
        }
    },

    methods: {
        scrollToTop() {
            this.$nextTick(() => {
                this.$vuetify.goTo(0);
            });
        },
        selectSubCategory(subCategory) {
            this.$router.push({ name: 'ProductCategory', params: { id: subCategory.id } });
        },
        loadMoreItems() {
            store.dispatch("catalog/openQuery");
        },
        async applyBrandFilter() {
            await store.dispatch("catalog/applyBrandFilter", this.selectedBrands);
            this.scrollToTop();
        },
        async setAttributeFilter() {
            let allSelectedAttributeValues = [];
            if (this.currentCategory?.filterOptions?.attributes) {
                allSelectedAttributeValues = this.currentCategory.filterOptions.attributes.reduce((acc, attr) => {
                    if (attr.selectedValues) {
                        acc.push(...attr.selectedValues);
                    }
                    return acc;
                }, []);
            }
            await store.dispatch("catalog/applyAttributeFilter", allSelectedAttributeValues);
            this.scrollToTop();
        },
        async clearFilter() {
            this.selectedBrands = [];
            if (this.currentCategory?.filterOptions?.attributes) {
                this.currentCategory.filterOptions.attributes.forEach(attr => attr.selectedValues = []);
            }
            await store.dispatch("catalog/clearFilters");
            this.scrollToTop();
        },
    },
};
</script>

<style scoped>
.sticky-left {
    position: -webkit-sticky;
    position: sticky;
    top: 204px;
    z-index: 1;
}
</style> 